// Gantt: rows per task, drag to move, edge-drag to resize, SVG dependency
// arrows (blocked_by -> task), today line, day/week/month zoom.
import { useMemo, useRef, useState } from 'react';
import { tasksAPI } from '../lib/api';
import { addDays, diffDays, parseISO, todayISO } from '../lib/dates';

const ZOOMS = { day: 36, week: 14, month: 5 };
const ROW_H = 34;
const EDGE = 7;

function taskSpan(t) {
  // bars need both ends; single-dated tasks render as 1-day bars
  const start = t.start_date || t.due_date;
  const end = t.due_date || t.start_date;
  if (!start) return null;
  return { start, end: end < start ? start : end };
}

export default function TimelineView({ groups, tasks, readOnly, onOpen, onChanged }) {
  const [zoom, setZoom] = useState('week');
  const [drag, setDrag] = useState(null); // {taskId, mode: move|left|right, startX, orig:{start,end}}
  const [preview, setPreview] = useState({}); // taskId -> {start,end}
  const scrollRef = useRef(null);
  const px = ZOOMS[zoom];

  const rows = useMemo(() => {
    const out = [];
    groups.forEach((g) => {
      const inGroup = tasks
        .filter((t) => t.group_id === g.id)
        .sort((a, b) => a.order - b.order);
      if (inGroup.length) out.push({ group: g, tasks: inGroup });
    });
    return out;
  }, [groups, tasks]);

  const flat = useMemo(() => rows.flatMap((r) => r.tasks), [rows]);
  const dated = useMemo(() => flat.filter((t) => taskSpan(t)), [flat]);

  const range = useMemo(() => {
    const today = todayISO();
    let min = today;
    let max = today;
    dated.forEach((t) => {
      const s = taskSpan(t);
      if (s.start < min) min = s.start;
      if (s.end > max) max = s.end;
    });
    return { start: addDays(min, -7), end: addDays(max, 21) };
  }, [dated]);

  const totalDays = diffDays(range.start, range.end) + 1;
  const width = totalDays * px;

  const spanOf = (t) => preview[t.id] || taskSpan(t);
  const xOf = (iso) => diffDays(range.start, iso) * px;

  // row index per task id (for arrow endpoints)
  const rowIndex = useMemo(() => {
    const map = {};
    let i = 0;
    rows.forEach((r) => {
      i += 1; // group header row
      r.tasks.forEach((t) => {
        map[t.id] = i;
        i += 1;
      });
    });
    return map;
  }, [rows]);
  const totalRows = useMemo(
    () => rows.reduce((acc, r) => acc + 1 + r.tasks.length, 0),
    [rows]
  );

  const startDrag = (e, task, mode) => {
    if (readOnly) return;
    e.preventDefault();
    e.stopPropagation();
    const span = taskSpan(task);
    if (!span) return;
    setDrag({ taskId: task.id, mode, startX: e.clientX, orig: span, moved: false });
  };

  const onPointerMove = (e) => {
    if (!drag) return;
    const delta = Math.round((e.clientX - drag.startX) / px);
    if (delta !== 0 && !drag.moved) setDrag({ ...drag, moved: true });
    let { start, end } = drag.orig;
    if (drag.mode === 'move') {
      start = addDays(start, delta);
      end = addDays(end, delta);
    } else if (drag.mode === 'left') {
      start = addDays(start, delta);
      if (start > end) start = end;
    } else {
      end = addDays(end, delta);
      if (end < start) end = start;
    }
    setPreview((p) => ({ ...p, [drag.taskId]: { start, end } }));
  };

  const onPointerUp = async () => {
    if (!drag) return;
    const p = preview[drag.taskId];
    const wasMoved = drag.moved;
    const taskId = drag.taskId;
    setDrag(null);
    if (!wasMoved || !p) {
      setPreview((prev) => {
        const { [taskId]: _, ...rest } = prev;
        return rest;
      });
      if (!wasMoved) onOpen(taskId);
      return;
    }
    try {
      await tasksAPI.patch(taskId, { start_date: p.start, due_date: p.end });
      await onChanged();
    } finally {
      setPreview((prev) => {
        const { [taskId]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  // dependency arrows
  const arrows = useMemo(() => {
    const list = [];
    dated.forEach((t) => {
      ((t.dependencies || {}).blocked_by || []).forEach((bid) => {
        const blocker = flat.find((x) => x.id === bid);
        if (!blocker) return;
        const bs = spanOf(blocker);
        const ts = spanOf(t);
        if (!bs || !ts || rowIndex[bid] === undefined || rowIndex[t.id] === undefined) return;
        list.push({
          key: `${bid}->${t.id}`,
          x1: xOf(bs.end) + px,
          y1: rowIndex[bid] * ROW_H + ROW_H / 2,
          x2: xOf(ts.start),
          y2: rowIndex[t.id] * ROW_H + ROW_H / 2,
        });
      });
    });
    return list;
  }, [dated, flat, rowIndex, preview, px, range.start]);

  const today = todayISO();
  const todayX = xOf(today);

  // month labels along the axis
  const months = useMemo(() => {
    const out = [];
    let cursor = range.start;
    while (cursor <= range.end) {
      const d = parseISO(cursor);
      const label = d.toLocaleDateString(undefined, { month: 'short', year: '2-digit', timeZone: 'UTC' });
      const nextMonth = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1));
      const endIso = nextMonth.toISOString().slice(0, 10);
      const span = Math.min(diffDays(cursor, range.end) + 1, diffDays(cursor, endIso));
      out.push({ label: `${label}`, x: xOf(cursor), w: span * px });
      cursor = endIso;
    }
    return out;
  }, [range.start, range.end, px]);

  return (
    <div className="flex h-full flex-col" onPointerMove={onPointerMove} onPointerUp={onPointerUp}>
      <div className="mb-2 flex items-center gap-1">
        {Object.keys(ZOOMS).map((z) => (
          <button
            key={z}
            type="button"
            onClick={() => setZoom(z)}
            className={`rounded-md px-2 py-1 text-xs capitalize ${
              zoom === z ? 'bg-pm-raised text-pm-text' : 'text-pm-muted hover:text-pm-text'
            }`}
          >
            {z}
          </button>
        ))}
        <span className="ml-3 text-xs text-pm-faint">
          {dated.length} scheduled · {flat.length - dated.length} without dates (set dates from the task panel)
        </span>
      </div>
      <div ref={scrollRef} className="min-h-0 flex-1 overflow-auto rounded-lg border border-pm-border bg-pm-surface">
        <div className="flex">
          {/* fixed name column */}
          <div className="sticky left-0 z-10 w-52 shrink-0 border-r border-pm-border bg-pm-surface">
            <div className="h-7 border-b border-pm-border" />
            {rows.map((r) => (
              <div key={r.group.id}>
                <div className="flex h-[34px] items-center gap-1.5 px-2 text-xs font-semibold">
                  <span className="h-2 w-2 rounded-full" style={{ background: r.group.color }} />
                  {r.group.name}
                </div>
                {r.tasks.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => onOpen(t.id)}
                    className={`block h-[34px] w-full truncate px-2 pl-5 text-left text-xs leading-[34px] text-pm-muted hover:text-pm-text ${
                      t.completed_at ? 'line-through opacity-60' : ''
                    }`}
                  >
                    {t.title}
                  </button>
                ))}
              </div>
            ))}
          </div>
          {/* chart */}
          <div className="relative" style={{ width, height: 28 + totalRows * ROW_H }}>
            <div className="sticky top-0 z-10 flex h-7 border-b border-pm-border bg-pm-surface text-[10px] text-pm-faint">
              {months.map((m) => (
                <div key={m.label + m.x} className="absolute flex h-7 items-center border-l border-pm-border/50 pl-1" style={{ left: m.x, width: m.w }}>
                  {m.label}
                </div>
              ))}
            </div>
            {/* weekend shading + grid */}
            {zoom !== 'month' &&
              Array.from({ length: totalDays }).map((_, i) => {
                const dow = parseISO(addDays(range.start, i)).getUTCDay();
                const weekend = dow === 0 || dow === 6;
                return weekend ? (
                  <div key={i} className="absolute top-7 bottom-0 bg-pm-bg/50" style={{ left: i * px, width: px }} />
                ) : null;
              })}
            {/* today line */}
            {today >= range.start && today <= range.end && (
              <div className="absolute top-7 bottom-0 w-px" style={{ left: todayX + px / 2, background: 'var(--pm-accent)' }} title="Today" />
            )}
            {/* dependency arrows */}
            <svg className="pointer-events-none absolute left-0 z-[5]" style={{ top: 28, width, height: totalRows * ROW_H }}>
              <defs>
                <marker id="dep-arrow" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
                  <path d="M0,0 L6,3 L0,6 z" fill="var(--pm-muted)" />
                </marker>
              </defs>
              {arrows.map((a) => (
                <path
                  key={a.key}
                  d={`M ${a.x1} ${a.y1} C ${a.x1 + 20} ${a.y1}, ${a.x2 - 20} ${a.y2}, ${a.x2 - 2} ${a.y2}`}
                  fill="none"
                  stroke="var(--pm-muted)"
                  strokeWidth="1.2"
                  markerEnd="url(#dep-arrow)"
                />
              ))}
            </svg>
            {/* rows + bars */}
            <div className="absolute left-0" style={{ top: 28 }}>
              {rows.map((r) => (
                <div key={r.group.id}>
                  <div style={{ height: ROW_H }} />
                  {r.tasks.map((t) => {
                    const span = spanOf(t);
                    if (!span) return <div key={t.id} style={{ height: ROW_H }} />;
                    const left = xOf(span.start);
                    const w = (diffDays(span.start, span.end) + 1) * px;
                    return (
                      <div key={t.id} className="relative" style={{ height: ROW_H }}>
                        <div
                          role="button"
                          tabIndex={0}
                          onPointerDown={(e) => startDrag(e, t, 'move')}
                          onKeyDown={(e) => e.key === 'Enter' && onOpen(t.id)}
                          className={`absolute top-1.5 flex h-[22px] items-center overflow-hidden rounded px-1.5 text-[11px] text-pm-accent-ink ${
                            readOnly ? '' : 'cursor-grab active:cursor-grabbing'
                          } ${t.completed_at ? 'opacity-50' : ''}`}
                          style={{ left, width: Math.max(w, 14), background: r.group.color }}
                          title={`${t.title} (${span.start} â†’ ${span.end})`}
                        >
                          {!readOnly && (
                            <span
                              onPointerDown={(e) => startDrag(e, t, 'left')}
                              className="absolute left-0 top-0 h-full cursor-ew-resize"
                              style={{ width: EDGE }}
                            />
                          )}
                          <span className="truncate">{w > 60 ? t.title : ''}</span>
                          {!readOnly && (
                            <span
                              onPointerDown={(e) => startDrag(e, t, 'right')}
                              className="absolute right-0 top-0 h-full cursor-ew-resize"
                              style={{ width: EDGE }}
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
